import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiEye, FiDownload, FiAward, FiPrinter } from 'react-icons/fi';
import './TranskripNilai.css';

const TranskripNilai = () => {
  const [transkripList, setTranskripList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [prodiFilter, setProdiFilter] = useState('');
  const [syncConfig, setSyncConfig] = useState({
    limit: 50,
    nim: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    avgIPK: 0,
    byProdi: []
  });

  useEffect(() => {
    fetchTranskrip();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, prodiFilter]);

  const fetchTranskrip = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (prodiFilter) params.prodi = prodiFilter;

      const res = await axios.get('/api/transkrip-nilai', { params });
      setTranskripList(res.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal mengambil data transkrip nilai');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/transkrip-nilai/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSyncTranskrip = async () => {
    setSyncing(true);
    try {
      const res = await axios.post('/api/neofeeder/sync/transkrip', syncConfig);
      toast.success(res.data.message || 'Sinkronisasi transkrip berhasil!');
      setShowSyncModal(false);
      fetchTranskrip();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Sinkronisasi transkrip gagal');
    } finally {
      setSyncing(false);
    }
  };

  const handlePrintTranskrip = async (nim) => {
    try {
      window.open(`/api/transkrip-nilai/print/${nim}`, '_blank');
    } catch (error) {
      toast.error('Gagal mencetak transkrip');
    }
  };

  const handleViewDetail = (nim) => {
    window.location.href = `/transkrip-nilai/detail/${nim}`;
  };

  const getIPKColor = (ipk) => {
    if (ipk >= 3.5) return '#10b981';
    if (ipk >= 3.0) return '#3b82f6';
    if (ipk >= 2.5) return '#f59e0b';
    return '#ef4444';
  };

  if (loading && transkripList.length === 0) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="transkrip-nilai-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiAward className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Transkrip Nilai</h1>
              <p className="header-subtitle">Kelola dan cetak transkrip nilai mahasiswa</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass btn-success" onClick={() => setShowSyncModal(true)}>
              <FiDownload /> 
              <span className="btn-text">Sinkronisasi</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#4f46e5' }}>
            <FiAward />
          </div>
          <div className="stat-details">
            <h3>{stats.total || 0}</h3>
            <p>Total Mahasiswa</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b981' }}>
            <FiDownload />
          </div>
          <div className="stat-details">
            <h3>{stats.avgIPK ? stats.avgIPK.toFixed(2) : '0.00'}</h3>
            <p>Rata-rata IPK</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b' }}>
            <FiEye />
          </div>
          <div className="stat-details">
            <h3>{stats.byProdi?.length || 0}</h3>
            <p>Program Studi</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari NIM atau nama mahasiswa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="filter-select"
            value={prodiFilter}
            onChange={(e) => setProdiFilter(e.target.value)}
          >
            <option value="">Semua Program Studi</option>
            {stats.byProdi && stats.byProdi.map((prodi, idx) => (
              <option key={idx} value={prodi._id}>{prodi._id}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Transkrip Table */}
      <div className="card">
        <h3>Daftar Transkrip Nilai Mahasiswa</h3>
        {transkripList.length === 0 ? (
          <div className="empty-state">
            <p>
              {searchTerm || prodiFilter
                ? 'Tidak ada transkrip nilai yang sesuai dengan filter'
                : 'Belum ada data transkrip nilai.'}
            </p>
          </div>
        ) : (
          <div className="modern-table-container">
            <table className="modern-table accent-green">
              <thead>
                <tr>
                  <th>NIM</th>
                  <th>Nama Mahasiswa</th>
                  <th>Program Studi</th>
                  <th>Total SKS</th>
                  <th>IPK</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {transkripList.map((transkrip) => (
                  <tr key={transkrip._id}>
                    <td>{transkrip.nim || '-'}</td>
                    <td><strong>{transkrip.nama_mahasiswa || '-'}</strong></td>
                    <td>{transkrip.nama_program_studi || transkrip.program || '-'}</td>
                    <td>{transkrip.total_sks || 0}</td>
                    <td>
                      <span 
                        className="ipk-badge" 
                        style={{ 
                          background: getIPKColor(transkrip.ipk),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontWeight: '600'
                        }}
                      >
                        {transkrip.ipk ? transkrip.ipk.toFixed(2) : '0.00'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => handleViewDetail(transkrip.nim)}
                          title="Lihat Detail"
                        >
                          <FiEye />
                        </button>
                        <button
                          className="table-btn table-btn-primary"
                          onClick={() => handlePrintTranskrip(transkrip.nim)}
                          title="Cetak Transkrip"
                        >
                          <FiPrinter />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Sinkronisasi */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content modal-sync" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Sinkronisasi Transkrip Nilai</h3>
              <button className="modal-close" onClick={() => setShowSyncModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="sync-info">
                <p>Sinkronisasi data transkrip nilai mahasiswa dari NEO Feeder</p>
                <p className="sync-warning">⚠️ Proses ini akan mengambil data dari endpoint GetTranskripMahasiswa</p>
              </div>

              <div className="form-group">
                <label>Limit Data</label>
                <input
                  type="number"
                  className="form-control"
                  value={syncConfig.limit}
                  onChange={(e) => setSyncConfig({ ...syncConfig, limit: e.target.value })}
                  min="1"
                  max="1000"
                />
                <small className="form-text">Jumlah maksimal data yang akan disinkronkan (1-1000)</small>
              </div>

              <div className="form-group">
                <label>Filter NIM (Opsional)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Contoh: 2023123456"
                  value={syncConfig.nim}
                  onChange={(e) => setSyncConfig({ ...syncConfig, nim: e.target.value })}
                />
                <small className="form-text">Kosongkan untuk semua mahasiswa. Isi NIM tertentu untuk sinkronisasi mahasiswa spesifik.</small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSyncModal(false)}
                disabled={syncing}
              >
                Batal
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSyncTranskrip}
                disabled={syncing}
              >
                {syncing ? 'Menyinkronkan...' : 'Mulai Sinkronisasi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranskripNilai;
